import axios from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiBackgroundLoadingErrorsAtom,
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  areLiveMapIncidentPollListsEqual,
  createCatchErrorResult,
  createErrorResult,
  createSuccessResult,
  errors,
  IApiError,
  IIncidentCloseResponse,
  IncidentsApi,
  liveMapNewIncidentsAF,
  Log,
  normalizeErrors,
  selectedLiveMapMapRidesSF,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultFromException,
  useDrawers,
  useHasPermissions,
  useSystemParams,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";

export interface IFetchLiveMapIncidentsOptions {
  abortController?: AbortController;
  backgroundAction?: boolean;
}

export const useLiveMapIncidentsActions = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const { openEditFormDrawer } = useDrawers();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setBackgroundLoadingErrors = useSetRecoilState(
    apiBackgroundLoadingErrorsAtom,
  );
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setIncidents = useSetRecoilState(liveMapNewIncidentsAF(entity));

  const { hasPermissions: hasIncidentRead } = useHasPermissions({
    entity: "incident",
    action: "read",
  });
  const { enableIncidents } = useSystemParams();
  const isIncidentsEnabled = Boolean(enableIncidents);

  const { hasPermissions: hasIncidentUpdate } = useHasPermissions({
    entity: "incident",
    action: "update",
  });

  const fetchLiveMapIncidentsForRideIds = useEvent(
    async (
      rideIds: readonly number[],
      fetchOptions?: IFetchLiveMapIncidentsOptions,
    ): Promise<boolean> => {
      if (!hasIncidentRead || !isIncidentsEnabled) {
        setIncidents(prev => (prev.length ? [] : prev));
        return true;
      }

      const { abortController, backgroundAction } = fetchOptions ?? {};
      const ids = [...new Set(rideIds)].filter(id => id > 0);

      if (!ids.length) {
        setIncidents(prev => (prev.length ? [] : prev));
        return true;
      }

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);
          setLoading(true);
        }

        Log.debug(`Fetching live-map incidents for  ${ids.length} rides`);
        const apiResult = await IncidentsApi.fetchLiveMapNewIncidents(
          ids,
          abortController,
        );

        if (!apiResult) {
          if (!backgroundAction) {
            setErrors([t(errors.common.emptyApiResponse)]);
          }

          return false;
        }

        if (!apiResult.success) {
          const line =
            typeof apiResult.error === "string" && apiResult.error.length
              ? apiResult.error
              : t(errors.common.emptyApiResponse);

          if (backgroundAction) {
            setBackgroundLoadingErrors([line]);
          } else {
            setErrors([line]);
          }

          return false;
        }

        const nextIncidents = apiResult.data ?? [];
        setIncidents(prev =>
          areLiveMapIncidentPollListsEqual(prev, nextIncidents)
            ? prev
            : nextIncidents,
        );
        return true;
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled (live-map incidents)", error);
          return false;
        }

        Log.error("Failed fetching live-map incidents", error);

        if (backgroundAction) {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        } else {
          setErrors(normalizeErrors(error as IApiError, t));
        }

        return false;
      } finally {
        if (!backgroundAction) {
          setLoading(false);
        }
      }
    },
  );

  const closeIncidentOnMap = useEvent(
    async (
      incidentId: number,
    ): Promise<SimpleResult<IIncidentCloseResponse>> => {
      if (!hasIncidentUpdate || !isIncidentsEnabled || incidentId <= 0) {
        return createErrorResult<IIncidentCloseResponse>(
          errors.incident.closeNotAllowed,
          "close incident on map not allowed",
        );
      }

      try {
        setErrors(undefined);
        setLoading(true);
        const apiResult = await IncidentsApi.closeIncident(incidentId);

        if (!apiResult) {
          return simpleResultErrorEmptyResponse<IIncidentCloseResponse>(t);
        }

        if (!apiResult.success || !apiResult.data) {
          return simpleResultFromException<IIncidentCloseResponse>({
            error: apiResult.error,
            fallbackTranslationKey: errors.incident.failedCloseFromLiveMap,
          });
        }

        setIncidents(prev => prev.filter(i => i.id !== incidentId));
        return createSuccessResult(apiResult.data);
      } catch (error) {
        Log.error("Failed closing incident from live map", error);
        return createCatchErrorResult<IIncidentCloseResponse>(error, t);
      } finally {
        setLoading(false);
      }
    },
  );

  const openIncidentDrawerFromLiveMap = useEvent(
    async (
      incidentId: number,
      fallbackRideId?: number | null,
    ): Promise<void> => {
      if (!hasIncidentRead || !isIncidentsEnabled || incidentId <= 0) {
        return;
      }

      const rideIdsFromMap = mapRides.map(r => r.id).filter(id => id > 0);
      let rideIds: number[] = [];
      if (rideIdsFromMap.length > 0) {
        rideIds = rideIdsFromMap;
      } else if (fallbackRideId && fallbackRideId > 0) {
        rideIds = [fallbackRideId];
      }

      await openEditFormDrawer({
        entity: "incident",
        id: incidentId,
        onCloseAction:
          rideIds.length > 0
            ? () => {
                void fetchLiveMapIncidentsForRideIds(rideIds, {
                  backgroundAction: true,
                });
              }
            : undefined,
      });
    },
  );

  return {
    fetchLiveMapIncidentsForRideIds,
    closeIncidentOnMap,
    openIncidentDrawerFromLiveMap,
  };
};
